'use client';

import {
	AlertTriangle,
	BadgeCheck,
	CheckCircle2,
	Clock3,
	GraduationCap,
	Plus,
	RefreshCw,
	ShieldCheck,
	Target,
	Users
} from 'lucide-react';
import type { ComponentType, FormEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { api, getErrorMessage } from '$lib/api/client';
import type {
	CompleteLearningEnrollmentRequest,
	CreateLearningEnrollmentRequest,
	CreateLearningProgramRequest,
	LearningChecklistItem,
	LearningChecklistStatus,
	LearningEnrollmentItem,
	LearningEnrollmentStatus,
	LearningOperationsSummaryResponse,
	LearningPlaybook,
	LearningProgramInsight,
	LearningProgramStatus,
	LearningProgramType,
	LearningRiskItem,
	LearningRiskSeverity
} from '$lib/api/types';
import {
	Badge,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	EmptyState,
	Input,
	Label,
	PageHeader,
	Spinner
} from '$lib/ui';

type RequestState<T> =
	| { status: 'idle' | 'loading' }
	| { status: 'success'; data: T }
	| { status: 'error'; message: string };

type ProgramFormState = {
	type: LearningProgramType;
	title: string;
	description: string;
	targetRole: string;
	mandatory: boolean;
	dueDate: string;
	ownerName: string;
};

type EnrollmentFormState = {
	programId: string;
	employeeId: string;
	employeeName: string;
	department: string;
	dueDate: string;
};

const selectClassName =
	'border-input bg-background ring-offset-background focus-visible:ring-ring/35 h-10 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2';

function numberLabel(value: number | null | undefined): string {
	if (typeof value !== 'number' || !Number.isFinite(value)) return '0';
	return new Intl.NumberFormat('ko-KR').format(value);
}

function defaultDate(days = 30): string {
	const date = new Date();
	date.setDate(date.getDate() + days);
	return date.toISOString().slice(0, 10);
}

function dateLabel(value: string | null | undefined): string {
	if (!value) return '-';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return new Intl.DateTimeFormat('ko-KR', { month: '2-digit', day: '2-digit' }).format(date);
}

function programTypeLabel(type: LearningProgramType): string {
	const labels: Record<LearningProgramType, string> = {
		COMPLIANCE: '컴플라이언스',
		ONBOARDING: '온보딩',
		ROLE: '직무',
		LEADERSHIP: '리더십',
		UPSKILLING: '업스킬링',
		PERFORMANCE: '성과 개선'
	};
	return labels[type] ?? type;
}

function programStatusLabel(status: LearningProgramStatus): string {
	const labels: Record<LearningProgramStatus, string> = {
		DRAFT: '초안',
		OPEN: '운영중',
		ARCHIVED: '보관'
	};
	return labels[status] ?? status;
}

function enrollmentStatusLabel(status: LearningEnrollmentStatus): string {
	const labels: Record<LearningEnrollmentStatus, string> = {
		ASSIGNED: '배정',
		IN_PROGRESS: '진행중',
		COMPLETED: '수료',
		OVERDUE: '마감 초과',
		WAIVED: '면제'
	};
	return labels[status] ?? status;
}

function checklistStatusLabel(status: LearningChecklistStatus): string {
	if (status === 'READY') return '준비';
	if (status === 'WARNING') return '주의';
	return '대기';
}

function checklistVariant(status: LearningChecklistStatus): 'success' | 'warning' | 'outline' {
	if (status === 'READY') return 'success';
	if (status === 'WARNING') return 'warning';
	return 'outline';
}

function riskVariant(severity: LearningRiskSeverity): 'success' | 'warning' | 'outline' {
	if (severity === 'READY') return 'success';
	if (severity === 'WARNING') return 'warning';
	return 'outline';
}

function statusVariant(status: LearningProgramStatus | LearningEnrollmentStatus): 'success' | 'warning' | 'outline' {
	if (status === 'OPEN' || status === 'COMPLETED') return 'success';
	if (status === 'OVERDUE' || status === 'IN_PROGRESS' || status === 'ASSIGNED') return 'warning';
	return 'outline';
}

function StatCard({
	title,
	value,
	description,
	icon: Icon
}: {
	title: string;
	value: number | string;
	description: string;
	icon: ComponentType<{ className?: string }>;
}) {
	return (
		<Card>
			<CardContent className="flex min-h-32 flex-col justify-between p-4">
				<div className="flex items-center justify-between gap-3">
					<p className="text-xs font-medium text-muted-foreground">{title}</p>
					<div className="rounded-md bg-muted p-2 text-muted-foreground">
						<Icon className="size-4" />
					</div>
				</div>
				<div>
					<p className="text-2xl font-semibold tracking-normal">
						{typeof value === 'number' ? numberLabel(value) : value}
					</p>
					<p className="mt-1 text-xs text-muted-foreground">{description}</p>
				</div>
			</CardContent>
		</Card>
	);
}

function ChecklistGrid({ items }: { items: LearningChecklistItem[] }) {
	if (items.length === 0) return <EmptyState message="교육 운영 체크리스트가 없습니다." />;
	return (
		<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
			{items.map((item) => {
				const Icon = item.status === 'READY' ? CheckCircle2 : item.status === 'WARNING' ? AlertTriangle : Clock3;
				return (
					<div key={item.code} className="min-w-0 rounded-lg border border-border bg-background p-4">
						<div className="flex items-start justify-between gap-3">
							<div className="flex min-w-0 items-start gap-3">
								<div className="mt-0.5 rounded-md bg-muted p-2 text-muted-foreground">
									<Icon className="size-4" />
								</div>
								<div className="min-w-0">
									<p className="font-semibold">{item.label}</p>
									<p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
								</div>
							</div>
							<Badge variant={checklistVariant(item.status)}>{checklistStatusLabel(item.status)}</Badge>
						</div>
					</div>
				);
			})}
		</div>
	);
}

function RiskPanel({ risks }: { risks: LearningRiskItem[] }) {
	if (risks.length === 0) return <EmptyState message="표시할 교육 운영 리스크가 없습니다." />;
	return (
		<div className="min-w-0 rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<AlertTriangle className="size-4 text-muted-foreground" />
				<p className="font-semibold">교육 리스크</p>
			</div>
			<div className="mt-4 space-y-3">
				{risks.map((risk) => (
					<div key={risk.code} className="flex items-start justify-between gap-3">
						<div className="min-w-0">
							<p className="font-medium">{risk.title}</p>
							<p className="mt-1 text-sm text-muted-foreground">{risk.actionLabel}</p>
						</div>
						<Badge variant={riskVariant(risk.severity)}>{numberLabel(risk.count)}</Badge>
					</div>
				))}
			</div>
		</div>
	);
}

function ProgramTable({ programs }: { programs: LearningProgramInsight[] }) {
	if (programs.length === 0) return <EmptyState message="등록된 교육 프로그램이 없습니다." />;
	return (
		<div className="max-w-full overflow-x-auto rounded-lg border border-border">
			<table className="w-full min-w-[820px] text-left text-sm">
				<thead className="bg-muted/60 text-xs text-muted-foreground">
					<tr>
						<th className="px-4 py-3 font-medium">프로그램</th>
						<th className="px-4 py-3 font-medium">대상</th>
						<th className="px-4 py-3 font-medium">수료율</th>
						<th className="px-4 py-3 font-medium">마감</th>
						<th className="px-4 py-3 font-medium">상태</th>
					</tr>
				</thead>
				<tbody>
					{programs.map((program) => (
						<tr key={program.programId} className="border-t border-border align-top">
							<td className="px-4 py-4">
								<div className="flex flex-wrap items-center gap-2">
									<p className="font-semibold">{program.title}</p>
									<Badge variant="outline">{programTypeLabel(program.type)}</Badge>
									{program.mandatory ? <Badge variant="warning">필수</Badge> : null}
								</div>
								<p className="mt-1 text-xs text-muted-foreground">담당 {program.ownerName}</p>
							</td>
							<td className="px-4 py-4 text-muted-foreground">{program.targetRole}</td>
							<td className="px-4 py-4">
								<p className="font-semibold">{numberLabel(program.completionRatePercent)}%</p>
								<p className="mt-1 text-xs text-muted-foreground">
									{numberLabel(program.completedCount)} / {numberLabel(program.enrollmentCount)}명
								</p>
							</td>
							<td className="px-4 py-4 text-muted-foreground">{dateLabel(program.dueDate)}</td>
							<td className="px-4 py-4">
								<div className="flex flex-wrap items-center gap-2">
									<Badge variant={statusVariant(program.status)}>{programStatusLabel(program.status)}</Badge>
									<Badge variant={program.healthLabel === '주의' ? 'warning' : 'outline'}>
										{program.healthLabel}
									</Badge>
								</div>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function EnrollmentTable({
	enrollments,
	actionId,
	onComplete
}: {
	enrollments: LearningEnrollmentItem[];
	actionId: number | null;
	onComplete: (enrollment: LearningEnrollmentItem) => void;
}) {
	if (enrollments.length === 0) return <EmptyState message="배정된 수강 대상자가 없습니다." />;
	return (
		<div className="max-w-full overflow-x-auto rounded-lg border border-border">
			<table className="w-full min-w-[820px] text-left text-sm">
				<thead className="bg-muted/60 text-xs text-muted-foreground">
					<tr>
						<th className="px-4 py-3 font-medium">대상자</th>
						<th className="px-4 py-3 font-medium">프로그램</th>
						<th className="px-4 py-3 font-medium">마감</th>
						<th className="px-4 py-3 font-medium">상태</th>
						<th className="px-4 py-3 font-medium">작업</th>
					</tr>
				</thead>
				<tbody>
					{enrollments.map((enrollment) => (
						<tr key={enrollment.enrollmentId} className="border-t border-border align-top">
							<td className="px-4 py-4">
								<p className="font-semibold">{enrollment.employeeName}</p>
								<p className="mt-1 text-xs text-muted-foreground">{enrollment.department}</p>
							</td>
							<td className="px-4 py-4 text-muted-foreground">{enrollment.programTitle ?? '-'}</td>
							<td className="px-4 py-4 text-muted-foreground">{dateLabel(enrollment.dueDate)}</td>
							<td className="px-4 py-4">
								<div className="flex flex-wrap items-center gap-2">
									<Badge variant={statusVariant(enrollment.status)}>{enrollmentStatusLabel(enrollment.status)}</Badge>
									<Badge variant={enrollment.healthLabel === '마감 초과' ? 'warning' : 'outline'}>
										{enrollment.healthLabel}
									</Badge>
								</div>
								{enrollment.completedAt ? (
									<p className="mt-1 text-xs text-muted-foreground">수료 {dateLabel(enrollment.completedAt)}</p>
								) : null}
							</td>
							<td className="px-4 py-4">
								<Button
									type="button"
									size="sm"
									variant="outline"
									disabled={
										enrollment.status === 'COMPLETED' ||
										enrollment.status === 'WAIVED' ||
										actionId === enrollment.enrollmentId
									}
									onClick={() => onComplete(enrollment)}
								>
									<BadgeCheck className="size-4" />
									수료
								</Button>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function PlaybookGrid({ playbooks }: { playbooks: LearningPlaybook[] }) {
	if (playbooks.length === 0) return <EmptyState message="교육 운영 플레이북이 없습니다." />;
	return (
		<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
			{playbooks.map((playbook) => (
				<div key={playbook.code} className="min-w-0 rounded-lg border border-border bg-background p-4">
					<div className="rounded-md bg-muted p-2 text-muted-foreground">
						<ShieldCheck className="size-4" />
					</div>
					<p className="mt-3 font-semibold">{playbook.label}</p>
					<p className="mt-1 text-sm text-muted-foreground">{playbook.description}</p>
				</div>
			))}
		</div>
	);
}

export function LearningOperationsPage() {
	const [state, setState] = useState<RequestState<LearningOperationsSummaryResponse>>({ status: 'idle' });
	const [actionMessage, setActionMessage] = useState<string | null>(null);
	const [operationLoading, setOperationLoading] = useState<string | null>(null);
	const [enrollmentActionId, setEnrollmentActionId] = useState<number | null>(null);
	const [programForm, setProgramForm] = useState<ProgramFormState>({
		type: 'COMPLIANCE',
		title: '',
		description: '',
		targetRole: '전사',
		mandatory: true,
		dueDate: defaultDate(),
		ownerName: 'People Ops'
	});
	const [enrollmentForm, setEnrollmentForm] = useState<EnrollmentFormState>({
		programId: '',
		employeeId: '',
		employeeName: '',
		department: '',
		dueDate: defaultDate()
	});

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const summary = await api.get<LearningOperationsSummaryResponse>('/learning/summary');
				setState({ status: 'success', data: summary });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	async function submitProgram(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setOperationLoading('program');
		setActionMessage(null);
		try {
			const request: CreateLearningProgramRequest = {
				type: programForm.type,
				title: programForm.title.trim(),
				description: programForm.description.trim() || null,
				targetRole: programForm.targetRole.trim(),
				mandatory: programForm.mandatory,
				dueDate: programForm.dueDate,
				ownerName: programForm.ownerName.trim()
			};
			await api.post('/learning/programs', request);
			setProgramForm((current) => ({ ...current, title: '', description: '', dueDate: defaultDate() }));
			setActionMessage('교육 프로그램을 생성했습니다.');
			await load();
		} catch (error) {
			setActionMessage(getErrorMessage(error));
		} finally {
			setOperationLoading(null);
		}
	}

	async function submitEnrollment(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setOperationLoading('enrollment');
		setActionMessage(null);
		try {
			const request: CreateLearningEnrollmentRequest = {
				employeeId: Number(enrollmentForm.employeeId),
				employeeName: enrollmentForm.employeeName.trim(),
				department: enrollmentForm.department.trim(),
				dueDate: enrollmentForm.dueDate
			};
			await api.post(`/learning/programs/${Number(enrollmentForm.programId)}/enrollments`, request);
			setEnrollmentForm((current) => ({
				...current,
				employeeId: '',
				employeeName: '',
				department: '',
				dueDate: defaultDate()
			}));
			setActionMessage('수강 대상자를 배정했습니다.');
			await load();
		} catch (error) {
			setActionMessage(getErrorMessage(error));
		} finally {
			setOperationLoading(null);
		}
	}

	async function completeEnrollment(enrollment: LearningEnrollmentItem) {
		setEnrollmentActionId(enrollment.enrollmentId);
		setActionMessage(null);
		try {
			const request: CompleteLearningEnrollmentRequest = {
				completionNote: `${enrollment.employeeName} 수료 처리`
			};
			await api.patch(`/learning/enrollments/${enrollment.enrollmentId}/complete`, request);
			setActionMessage('수강 상태를 수료로 변경했습니다.');
			await load();
		} catch (error) {
			setActionMessage(getErrorMessage(error));
		} finally {
			setEnrollmentActionId(null);
		}
	}

	const data = state.status === 'success' ? state.data : null;

	return (
		<div className="min-w-0 space-y-6 overflow-x-hidden">
			<PageHeader
				title="교육"
				description="필수교육, 직무별 업스킬링, 리더십 enablement, 수강 배정과 수료 리스크를 관리합니다."
				actions={
					<Button type="button" variant="outline" onClick={() => void load()}>
						<RefreshCw className="size-4" />
						새로고침
					</Button>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{actionMessage ? (
				<div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm">{actionMessage}</div>
			) : null}

			{data ? (
				<>
					<div className="grid min-w-0 grid-cols-2 gap-3 lg:grid-cols-4">
						<StatCard
							title="운영 프로그램"
							value={data.openProgramCount}
							description={`전체 ${numberLabel(data.totalProgramCount)}개 프로그램`}
							icon={GraduationCap}
						/>
						<StatCard
							title="필수교육"
							value={data.mandatoryProgramCount}
							description="컴플라이언스 커버리지"
							icon={ShieldCheck}
						/>
						<StatCard
							title="수료율"
							value={`${numberLabel(data.completionRatePercent)}%`}
							description={`${numberLabel(data.completedEnrollmentCount)} / ${numberLabel(data.totalEnrollmentCount)}명 수료`}
							icon={BadgeCheck}
						/>
						<StatCard
							title="역량 갭"
							value={data.skillGapCount}
							description={`마감 초과 ${numberLabel(data.overdueEnrollmentCount)}건`}
							icon={Target}
						/>
					</div>

					<div className="grid min-w-0 gap-4 xl:grid-cols-2">
						<Card className="min-w-0">
							<CardHeader>
								<CardTitle>프로그램 생성</CardTitle>
								<CardDescription>필수교육, 직무교육, 리더십 교육을 운영 가능한 프로그램으로 등록합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<form className="grid gap-3" onSubmit={submitProgram}>
									<div className="grid gap-3 sm:grid-cols-2">
										<div>
											<Label htmlFor="learning-program-type">유형</Label>
											<select
												id="learning-program-type"
												className={selectClassName}
												value={programForm.type}
												onChange={(event) =>
													setProgramForm((current) => ({
														...current,
														type: event.target.value as LearningProgramType
													}))
												}
											>
												<option value="COMPLIANCE">컴플라이언스</option>
												<option value="ONBOARDING">온보딩</option>
												<option value="ROLE">직무</option>
												<option value="LEADERSHIP">리더십</option>
												<option value="UPSKILLING">업스킬링</option>
												<option value="PERFORMANCE">성과 개선</option>
											</select>
										</div>
										<div>
											<Label htmlFor="learning-owner">담당자</Label>
											<Input
												id="learning-owner"
												value={programForm.ownerName}
												onChange={(event) => setProgramForm((current) => ({ ...current, ownerName: event.target.value }))}
												required
											/>
										</div>
									</div>
									<div>
										<Label htmlFor="learning-title">프로그램명</Label>
										<Input
											id="learning-title"
											value={programForm.title}
											onChange={(event) => setProgramForm((current) => ({ ...current, title: event.target.value }))}
											placeholder="예: 개인정보보호 필수교육"
											required
										/>
									</div>
									<div>
										<Label htmlFor="learning-description">설명</Label>
										<textarea
											id="learning-description"
											className="border-input bg-background ring-offset-background focus-visible:ring-ring/35 min-h-20 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
											value={programForm.description}
											onChange={(event) =>
												setProgramForm((current) => ({ ...current, description: event.target.value }))
											}
										/>
									</div>
									<div className="grid gap-3 sm:grid-cols-2">
										<div>
											<Label htmlFor="learning-target-role">대상</Label>
											<Input
												id="learning-target-role"
												value={programForm.targetRole}
												onChange={(event) =>
													setProgramForm((current) => ({ ...current, targetRole: event.target.value }))
												}
												required
											/>
										</div>
										<div>
											<Label htmlFor="learning-due-date">마감일</Label>
											<Input
												id="learning-due-date"
												type="date"
												value={programForm.dueDate}
												onChange={(event) =>
													setProgramForm((current) => ({ ...current, dueDate: event.target.value }))
												}
												required
											/>
										</div>
									</div>
									<label className="flex items-center gap-2 text-sm">
										<input
											type="checkbox"
											checked={programForm.mandatory}
											onChange={(event) =>
												setProgramForm((current) => ({ ...current, mandatory: event.target.checked }))
											}
										/>
										필수교육
									</label>
									<Button type="submit" disabled={operationLoading === 'program'}>
										<Plus className="size-4" />
										프로그램 생성
									</Button>
								</form>
							</CardContent>
						</Card>

						<Card className="min-w-0">
							<CardHeader>
								<CardTitle>수강 배정</CardTitle>
								<CardDescription>프로그램 ID 기준으로 대상자를 배정하고 부서와 마감일을 기록합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<form className="grid gap-3" onSubmit={submitEnrollment}>
									<div className="grid gap-3 sm:grid-cols-2">
										<div>
											<Label htmlFor="learning-program-id">프로그램 ID</Label>
											<Input
												id="learning-program-id"
												type="number"
												value={enrollmentForm.programId}
												onChange={(event) =>
													setEnrollmentForm((current) => ({ ...current, programId: event.target.value }))
												}
												required
											/>
										</div>
										<div>
											<Label htmlFor="learning-employee-id">직원 ID</Label>
											<Input
												id="learning-employee-id"
												type="number"
												value={enrollmentForm.employeeId}
												onChange={(event) =>
													setEnrollmentForm((current) => ({ ...current, employeeId: event.target.value }))
												}
												required
											/>
										</div>
									</div>
									<div className="grid gap-3 sm:grid-cols-2">
										<div>
											<Label htmlFor="learning-employee-name">대상자</Label>
											<Input
												id="learning-employee-name"
												value={enrollmentForm.employeeName}
												onChange={(event) =>
													setEnrollmentForm((current) => ({ ...current, employeeName: event.target.value }))
												}
												required
											/>
										</div>
										<div>
											<Label htmlFor="learning-department">부서</Label>
											<Input
												id="learning-department"
												value={enrollmentForm.department}
												onChange={(event) =>
													setEnrollmentForm((current) => ({ ...current, department: event.target.value }))
												}
												required
											/>
										</div>
									</div>
									<div>
										<Label htmlFor="learning-enrollment-due">마감일</Label>
										<Input
											id="learning-enrollment-due"
											type="date"
											value={enrollmentForm.dueDate}
											onChange={(event) =>
												setEnrollmentForm((current) => ({ ...current, dueDate: event.target.value }))
											}
											required
										/>
									</div>
									<Button type="submit" disabled={operationLoading === 'enrollment'}>
										<Users className="size-4" />
										대상자 배정
									</Button>
								</form>
							</CardContent>
						</Card>
					</div>

					<div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,0.65fr)]">
						<Card className="min-w-0">
							<CardHeader>
								<CardTitle>운영 체크리스트</CardTitle>
								<CardDescription>프로그램 라이브러리, 필수교육 커버리지, 직무교육과 성과 연결성을 확인합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<ChecklistGrid items={data.checklist} />
							</CardContent>
						</Card>
						<RiskPanel risks={data.riskItems} />
					</div>

					<div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
						<Card className="min-w-0">
							<CardHeader>
								<CardTitle>프로그램 현황</CardTitle>
								<CardDescription>교육 유형, 대상 역할, 담당자, 수료율과 건강 상태를 확인합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<ProgramTable programs={data.programInsights} />
							</CardContent>
						</Card>
						<Card className="min-w-0">
							<CardHeader>
								<CardTitle>수강 보드</CardTitle>
								<CardDescription>대상자별 수강 상태, 마감일, 수료 처리 현황을 관리합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<EnrollmentTable
									enrollments={data.enrollmentBoard}
									actionId={enrollmentActionId}
									onComplete={(enrollment) => void completeEnrollment(enrollment)}
								/>
							</CardContent>
						</Card>
					</div>

					<Card className="min-w-0">
						<CardHeader>
							<CardTitle>교육 운영 플레이북</CardTitle>
							<CardDescription>필수교육, 직무 업스킬링, 리더십 enablement와 성과 개선 루틴을 실행합니다.</CardDescription>
						</CardHeader>
						<CardContent>
							<PlaybookGrid playbooks={data.playbooks} />
						</CardContent>
					</Card>
				</>
			) : null}
		</div>
	);
}
